import MediaRepository, { MediaQueryParams } from '@/repositories/media-repository';
import RelatedMediaRepository from '@/repositories/related-media-repository';
import { Media$Availability, Media$Details, Media$Privacy } from '@/types/Media';
import $t from '@Ravnur-Inc/l10n/$t';
import { Options, Vue, setup } from 'vue-class-component';

import MediaSublist from '../media-sublist/media-sublist';
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import { Media$RelatedLink } from '@Ravnur-Inc/shared/types/Media';

import './related-media.scss';

class Props {
  origin!: Media$Details;
}

const CN = 'related-media';
const mediaRepository = new MediaRepository();

@Options({
  validations: {
    name: { required, $autoDirty: true },
    url: { required, url, $autoDirty: true },
  },
})
export default class RelatedMedia extends Vue.with(Props) {
  public v$ = setup(() => useVuelidate());

  public name = '';
  public url = '';
  public localLinks: Media$RelatedLink[] = [];

  private get isLinkFormEmpty() {
    return !this.name && !this.url;
  }

  private get isLinkFormInvalid() {
    return this.v$.$invalid;
  }

  private get repository() {
    return new RelatedMediaRepository(this.origin.id);
  }

  private get queryParams(): Partial<MediaQueryParams> {
    return {
      availability: Media$Availability.AVAILABLE,
    };
  }

  render() {
    return (
      <div class={CN}>
        {this.renderLinks()}
        {this.renderRelatedMedia()}
      </div>
    );
  }

  renderRelatedMedia() {
    return (
      <>
        <l10n class={`${CN}__title`} group="media" tkey="related-media__add-media-label" />
        <MediaSublist
          class={CN}
          editText={$t('media', 'related-media__edit')}
          origin={this.origin}
          privacy={[Media$Privacy.PUBLIC, Media$Privacy.UNLISTED]}
          queryParams={this.queryParams}
          repository={this.repository}
        />
      </>
    );
  }

  renderLinks() {
    return (
      <>
        <div class={`${CN}__link-form`}>
          <l10n class={`${CN}__title`} group="media" tkey="related-link__add-link-label" />
          <text-field
            class={`${CN}__field`}
            errors={this.v$.name.$errors}
            maxLength={100}
            placeholder={$t('media', 'related-link__name-placeholder')}
            vModel={[this.name, 'value']}
          />
          <text-field
            class={`${CN}__field`}
            errors={this.v$.url.$errors}
            placeholder={$t('media', 'related-link__url-placeholder')}
            vModel={[this.url, 'value']}
          />
        </div>
        <div class={`${CN}__link-actions`}>
          <r-button
            color="primary"
            disabled={this.isLinkFormEmpty}
            mode="secondary"
            onclick={this.clearForm}
          >
            <l10n group="common" tkey="action__clear" />
          </r-button>
          <action-button
            class={`${CN}__add-link`}
            color="primary"
            disabled={this.isLinkFormInvalid}
            process={this.addLink}
          >
            <l10n group="media" tkey="related-link__add-link-btn" />
          </action-button>
        </div>
        <div class={`${CN}__links-wrapper`}>
          <ul class={`${CN}__links`}>{this.localLinks.map(this.renderRelatedLink)}</ul>
        </div>
      </>
    );
  }

  renderDeleteButton(action: () => void) {
    return <r-button class={`${CN}__delete-btn`} icon="trash" mode="frameless" onclick={action} />;
  }

  renderRelatedLink(l: Media$RelatedLink) {
    return (
      <li class={`${CN}__link-wrapper`}>
        <span class={`${CN}__link-name`}>{l.name}</span>
        <a class={`${CN}__link-path`} href={l.path} rel="noreferrer" target="_blank">
          {l.path}
        </a>
        {this.renderDeleteButton(() => this.removeLink(l))}
      </li>
    );
  }

  async addLink() {
    if (!(await this.v$.$validate?.())) {
      return;
    }

    const { name, url: path } = this;
    const link = { name, path };

    try {
      await mediaRepository.save({ ...this.origin, relatedLinks: [...this.localLinks, link] });

      this.localLinks.push(link);

      this.name = '';
      this.url = '';
      this.v$.$reset();
    } catch (e) {
      this.$processException(e);
    }
  }

  async removeLink(link: Media$RelatedLink) {
    this.localLinks = [...this.localLinks.filter((l) => l !== link)];

    try {
      await mediaRepository.save({ ...this.origin, relatedLinks: this.localLinks });
    } catch (e) {
      this.$processException(e);
    }
  }

  clearForm() {
    this.name = '';
    this.url = '';
    this.v$.$reset();
  }

  mounted() {
    this.localLinks = [...this.origin.relatedLinks];
  }
}
